import React, { useState } from 'react';
import {
    Portal,
    Tooltip,
    IconButton,
    TextField,
    Checkbox,
    FormControlLabel,
    CircularProgress
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import Select from 'react-select';

import Constants from '../../../assests/Constants/constants.js'; // Adjust path as needed
import UserService from '../../../Services/user.service.js';
import {
    XTRAM_COLORS,
    XTRAM_TYPOGRAPHY,
    XTRAM_RADIUS,
    XTRAM_SPACE,
    XTRAM_UI,
} from '../../../assests/branding/xtram/brand';
import styles from './ClientRegisterModal.module.scss';

/**
 * ClientRegisterModal
 *
 * PROPS:
 *  - open (bool) => controls modal visibility
 *  - onClose (function) => closes modal
 *  - t (function) => for i18n translations
 *  - setShowSuccessModal (function) => toggles success modal
 *  - direction (string) => "rtl" or "ltr" for text direction
 *  - toast (function) => for toast notifications
 */
function ClientRegisterModal({
    open,
    onClose,
    t,
    setShowSuccessModal,
    direction = 'ltr',
    toast,
}) {
    // --- EXACT state from your snippet ---
    const [addClientItem, setAddClientItem] = useState({
        clientName: '',
        caseID: '',
        clientID: '',
        taxClientNumber: '',
        vatClientNumber: '',
        officeIdentifyNumber: null,
        deductionsCase: '',
        taxCreditPoints: '2.25',
        clientPassword: '',
        clientPhoneNumber: '',
        clientLocation: '',
        clientEmail: '',
        clientWebsite: '',
        incomeTaxAdvancePercentage: '',
        incomeTaxAdvanceAmount: '',
        reportingType: '',
        advancedReportingType: '',
        accountManagement: '',
        caseType: '',
        subAccountantId: '',
        taxAssessor: '',
        // Business self-signup is for vehicle management — use that package on create.
        businessField: Constants.VehicleDealership,
        clientPaymentMethod: 1,
        package: Constants.packageOptionsEnum.Basic,
    });

    // Focus states for potential styling
    const [focusState, setFocusState] = useState({});

    // Toggle for IncomeTaxAdvancePercentage
    const [useIncomeTaxAdvancePercentage, setUseIncomeTaxAdvancePercentage] = useState(true);

    // Searching/focus states (if needed)
    const [isReportFocus, setIsReportFocus] = useState(false);
    const [isAdvancedReportFocus, setIsAdvancedReportFocus] = useState(false);
    const [isAccountManagementFocus, setIsAccountManagementFocus] = useState(false);
    const [isCaseTypeFocus, setIsCaseTypeFocus] = useState(false);
    const [isSubAccFocus, setIsSubAccFocus] = useState(false);
    const [isTaxAssessorFocus, setIsTaxAssessorFocus] = useState(false);
    const [isPaymentFocus, setIsPaymentFocus] = useState(false);
    const [isPaymentMethodFocus, setIsPaymentMethodFocus] = useState(false);
    const [isPackageFocus, setIsPackageFocus] = useState(false);
    const [isSearching, setIsSearching] = useState(false);

    // Show spinner while saving
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Check whether Save is enabled
     */
    const caseIdIsValid = /^\d{9,10}$/.test(String(addClientItem.caseID || ''));
    const addClientEnabled = Boolean(
        addClientItem.clientName &&
        caseIdIsValid &&
        addClientItem.clientID &&
        addClientItem.clientPassword &&
        addClientItem.clientPhoneNumber &&
        addClientItem.advancedReportingType &&
        addClientItem.reportingType &&
        addClientItem.accountManagement &&
        addClientItem.caseType &&
        addClientItem.package
    );

    const handleFocus = (field) => {
        setFocusState((prev) => ({ ...prev, [field]: true }));
    };
    const handleBlur = (field) => {
        setFocusState((prev) => ({ ...prev, [field]: false }));
    };

    const handleAddClientChange = (field, value) => {
        const nextValue = field === 'caseID'
            ? String(value ?? '').replace(/\D/g, '').slice(0, 10)
            : value;
        setAddClientItem((prev) => ({
            ...prev,
            [field]: nextValue,
        }));
    };

    /**
     * Cancel => reset form + close
     */
    const handleAddClientCancel = () => {
        setFocusState({});
        setAddClientItem({
            clientName: '',
            caseID: '',
            clientID: '',
            taxClientNumber: '',
            vatClientNumber: '',
            officeIdentifyNumber: null,
            deductionsCase: '',
            taxCreditPoints: '2.25',
            clientPassword: '',
            clientPhoneNumber: '',
            clientLocation: '',
            clientEmail: '',
            clientWebsite: '',
            incomeTaxAdvancePercentage: '',
            incomeTaxAdvanceAmount: '',
            reportingType: '',
            advancedReportingType: '',
            accountManagement: '',
            caseType: '',
            subAccountantId: '',
            taxAssessor: '',
            businessField: Constants.VehicleDealership,
            clientPaymentMethod: 1,
            package: Constants.packageOptionsEnum.Basic,
        });
        onClose && onClose();
    };

    /**
     * On success => close + show success
     */
    const handleSuccessAddClient = () => {
        handleAddClientCancel();
        if (setShowSuccessModal) {
            setTimeout(() => {
                setShowSuccessModal(true);
            }, 500);
        }
    };

    /**
     * handleClientSave => main logic from snippet
     */
    const handleClientSave = async () => {
        if (!addClientEnabled) return;
        if (!caseIdIsValid) {
            toast?.error(t ? t('CaseIdDigitsRange') : 'Case ID must be 9 or 10 digits');
            return;
        }
        setIsLoading(true);

        // Vehicle/Garage are self-paying (Business) models.
        const isBusinessPayer =
            addClientItem.businessField === Constants.VehicleDealership ||
            addClientItem.businessField === Constants.Garage;
        const finalClientPaymentMethod = isBusinessPayer ? 1 : addClientItem.clientPaymentMethod;

        // Build final data object
        const clientData = {
            caseId: addClientItem.caseID,
            accountantId: Constants.XtramAccountantId, // replaced user.accountantId
            clientId: addClientItem.clientID,
            businessField: addClientItem.businessField,
            taxClientNumber: addClientItem.taxClientNumber?.trim() || null,
            vatClientNumber: addClientItem.vatClientNumber?.trim() || null,
            name: addClientItem.clientName,
            password: addClientItem.clientPassword,
            phoneNumber: addClientItem.clientPhoneNumber,
            caseType: addClientItem.caseType,
            AccountManagement: addClientItem.accountManagement,
            SubAccountantId: addClientItem.subAccountantId,
            IncomeTaxAdvancePercentage: parseFloat(addClientItem.incomeTaxAdvancePercentage),
            IncomeTaxAdvanceAmount: parseFloat(addClientItem.incomeTaxAdvanceAmount),
            UseIncomeTaxAdvancePercentage: useIncomeTaxAdvancePercentage,
            Website: addClientItem.clientWebsite,
            Location: addClientItem.clientLocation,
            Email: addClientItem.clientEmail,
            ReportingType: addClientItem.reportingType,
            deductionsCase: addClientItem.deductionsCase,
            taxAssessor: addClientItem.taxAssessor?.toString(),
            taxCreditPoints: addClientItem.taxCreditPoints,
            clientPaymentMethod: finalClientPaymentMethod,
            advancedReportingType: addClientItem.advancedReportingType,
            PermissionsClient: addClientItem.package,
        };

        try {
            const response = await UserService.addClientForXtramAccountant(clientData);
            if (response.ok) {
                handleSuccessAddClient();
            } else {
                console.error(response);
                toast.error(
                    t ? t('failedToAddCustomer') : 'Failed to add customer'
                );
                handleAddClientCancel();
            }
        } catch (error) {
            console.error(error);
            toast.error(
                t ? t('clientSaveError') : 'Client save error'
            );
            handleAddClientCancel();
        }

        // Clear data
        setAddClientItem({});
        setIsLoading(false);
    };

    // If not open => don't render anything
    if (!open) return null;

    const lang =
        (typeof document !== 'undefined' && document.documentElement.lang) ||
        (direction === 'rtl' ? 'he' : 'en');

    const brandVars = {
        '--register-blue': XTRAM_COLORS.shockingBlue,
        '--register-blue-hover': XTRAM_COLORS.shockingBlueHover,
        '--register-orange': XTRAM_COLORS.classicOrange,
        '--register-mirage': XTRAM_COLORS.mirage,
        '--register-mirage-muted': XTRAM_COLORS.mirageMuted,
        '--register-white': XTRAM_COLORS.white,
        '--register-success': XTRAM_COLORS.successGreen,
        '--register-overlay': XTRAM_COLORS.overlaySoft,
        '--register-font': XTRAM_TYPOGRAPHY.fontForLang(lang),
        '--register-text-xs': XTRAM_TYPOGRAPHY.scale.xs,
        '--register-text-sm': XTRAM_TYPOGRAPHY.scale.sm,
        '--register-text-md': XTRAM_TYPOGRAPHY.scale.md,
        '--register-text-xl': XTRAM_TYPOGRAPHY.scale.xl,
        '--register-weight-label': XTRAM_TYPOGRAPHY.weights.semiBold,
        '--register-weight-heading': XTRAM_TYPOGRAPHY.weights.bold,
        '--register-radius': XTRAM_RADIUS.lg,
        '--register-radius-md': XTRAM_RADIUS.md,
        '--register-space-xs': XTRAM_SPACE.xs,
        '--register-space-sm': XTRAM_SPACE.sm,
        '--register-space-md': XTRAM_SPACE.md,
        '--register-space-lg': XTRAM_SPACE.lg,
        '--register-overflow-y': XTRAM_UI.scroll.overflowY,
        '--register-overflow-x': XTRAM_UI.scroll.overflowX,
        '--register-overflow-scrolling': XTRAM_UI.scroll.webkitOverflowScrolling,
        '--register-scroll-padding': XTRAM_UI.scroll.padding,
        '--register-scrollbar-width': XTRAM_UI.scroll.scrollbarWidth,
        '--register-scrollbar-color': XTRAM_UI.scroll.scrollbarColor,
    };

    const inputPropsStyle = {
        disableUnderline: true,
        className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
    };

    const inputStyle = {
        style: {
            textAlign: direction === 'rtl' ? 'right' : 'left',
            direction: direction === 'rtl' ? 'rtl' : 'ltr',
        },
    };

    // Convert constants to react-select-friendly arrays
    const reportingTypeOptions = (Constants.ReportingTypeOptions || []).map((opt) => ({
        value: opt.name,
        label: opt.name,
    }));

    const accountManagementOptions = (Constants.AccountManagementOptions || []).map(
        (opt) => ({
            value: opt.name,
            label: opt.name,
        })
    );

    const caseTypeOptions = (Constants.CaseTypeOptions || []).map((opt) => ({
        value: opt.name,
        label: opt.name,
    }));

    const packageOptions = (Constants.packageOptions || []).map((opt) => ({
        value: opt.value,
        label: t(opt.label),
    }));

    const businessFieldOptions = (Constants.BusinessFieldOptions || []).map((opt) => ({
        value: opt.value,
        label: t ? t(opt.name) : opt.name,
        name: opt.name,
    }));

    return (
        <Portal>
            {/* Full-screen Overlay with Blur */}
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                style={brandVars}
                onClick={handleAddClientCancel}
            >
                {/* Prevent click on modal from closing */}
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modal}>
                        <div className={styles.scroll}>
                        {/* Header */}
                        <div className={styles.header}>
                            <Tooltip title={t ? t('Exit') : 'Exit'}>
                                <IconButton onClick={handleAddClientCancel} className={styles.closeIcon}>
                                    <CancelIcon className={styles.closeIcon} />
                                </IconButton>
                            </Tooltip>
                            <div>{t ? t('Register') : 'Add Business'}</div>
                            <div />
                        </div>

                        {/* Row 1: caseID, clientID */}
                        <div className={styles.formRow}>
                            {/* CaseID */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('CaseID') : 'CaseID'} *
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    value={addClientItem.caseID}
                                    onChange={(e) => handleAddClientChange('caseID', e.target.value)}
                                    onFocus={() => handleFocus('caseID')}
                                    onBlur={() => handleBlur('caseID')}
                                    required
                                    error={!!addClientItem.caseID && !caseIdIsValid}
                                    helperText={addClientItem.caseID && !caseIdIsValid
                                        ? (t ? t('CaseIdDigitsRange') : 'Case ID must be 9 or 10 digits')
                                        : ''}
                                    FormHelperTextProps={{ sx: { color: '#d32f2f', mx: 0 } }}
                                    InputProps={inputPropsStyle}
                                    inputProps={{ ...inputStyle.style, inputMode: 'numeric', maxLength: 10, pattern: '[0-9]*' }}
                                    placeholder={t ? t('CaseID') : 'CaseID'}
                                />
                            </div>

                            {/* ClientID */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('ClientId') : 'ClientID'} *
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    type="number"
                                    value={addClientItem.clientID}
                                    onChange={(e) => handleAddClientChange('clientID', e.target.value)}
                                    onFocus={() => handleFocus('clientID')}
                                    onBlur={() => handleBlur('clientID')}
                                    required
                                    InputProps={inputPropsStyle}
                                    inputProps={{ ...inputStyle.style, inputMode: 'numeric' }}
                                    placeholder={t ? t('ClientId') : 'ClientId'}
                                />
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('TaxClientNumber') : 'Tax client number'}
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    value={addClientItem.taxClientNumber}
                                    onChange={(e) => handleAddClientChange('taxClientNumber', e.target.value)}
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle.style}
                                    placeholder={t ? t('TaxClientNumber') : 'Tax client number'}
                                />
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('VatClientNumber') : 'VAT business number'}
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    value={addClientItem.vatClientNumber}
                                    onChange={(e) => handleAddClientChange('vatClientNumber', e.target.value)}
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle.style}
                                    placeholder={t ? t('VatClientNumber') : 'VAT business number'}
                                />
                            </div>
                        </div>

                        {/* Row 3: clientName, clientPassword */}
                        <div className={styles.formRow}>
                            {/* clientName */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('enterName') : 'Client Name'} *
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    value={addClientItem.clientName}
                                    onChange={(e) => handleAddClientChange('clientName', e.target.value)}
                                    onFocus={() => handleFocus('clientName')}
                                    onBlur={() => handleBlur('clientName')}
                                    required
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle.style}
                                    placeholder={t ? t('enterName') : 'enterName'}
                                />
                            </div>

                            {/* clientPassword */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('enterPassword') : 'Client Password'} *
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    type="password"
                                    value={addClientItem.clientPassword}
                                    onChange={(e) =>
                                        handleAddClientChange('clientPassword', e.target.value)
                                    }
                                    onFocus={() => handleFocus('clientPassword')}
                                    onBlur={() => handleBlur('clientPassword')}
                                    required
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle.style}
                                    placeholder={t ? t('enterPassword') : 'enterPassword'}
                                />
                            </div>
                        </div>

                        {/* Row 4: clientPhoneNumber, taxCreditPoints */}
                        <div className={styles.formRow}>
                            {/* clientPhoneNumber */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('enterPhoneNumber') : 'Client Phone Number'} *
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    type="number"
                                    value={addClientItem.clientPhoneNumber}
                                    onChange={(e) =>
                                        handleAddClientChange('clientPhoneNumber', e.target.value)
                                    }
                                    onFocus={() => handleFocus('clientPhoneNumber')}
                                    onBlur={() => handleBlur('clientPhoneNumber')}
                                    required
                                    InputProps={inputPropsStyle}
                                    inputProps={{ ...inputStyle.style, inputMode: 'numeric' }}
                                    placeholder={t ? t('enterPhoneNumber') : 'enterPhoneNumber'}
                                />
                            </div>

                            {/* taxCreditPoints */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t
                                        ? t('IncomeTaxCreditPoints')
                                        : 'Income Tax Credit Points'}
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    type="number"
                                    value={addClientItem.taxCreditPoints}
                                    onChange={(e) =>
                                        handleAddClientChange('taxCreditPoints', e.target.value)
                                    }
                                    onFocus={() => handleFocus('taxCreditPoints')}
                                    onBlur={() => handleBlur('taxCreditPoints')}
                                    InputProps={inputPropsStyle}
                                    inputProps={{ ...inputStyle.style, inputMode: 'numeric' }}
                                    placeholder={
                                        t ? t('IncomeTaxCreditPoints') : 'IncomeTaxCreditPoints'
                                    }
                                />
                            </div>
                        </div>

                        {/* Row 5: clientEmail, clientLocation */}
                        <div className={styles.formRow}>
                            {/* clientEmail */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('Email') : 'Client Email'}
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    type="email"
                                    value={addClientItem.clientEmail}
                                    onChange={(e) =>
                                        handleAddClientChange('clientEmail', e.target.value)
                                    }
                                    onFocus={() => handleFocus('clientEmail')}
                                    onBlur={() => handleBlur('clientEmail')}
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle.style}
                                    placeholder={t ? t('Email') : 'Email'}
                                />
                            </div>

                            {/* clientLocation */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('Location') : 'Client Location'}
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    value={addClientItem.clientLocation}
                                    onChange={(e) =>
                                        handleAddClientChange('clientLocation', e.target.value)
                                    }
                                    onFocus={() => handleFocus('clientLocation')}
                                    onBlur={() => handleBlur('clientLocation')}
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle.style}
                                    placeholder={t ? t('Location') : 'Location'}
                                />
                            </div>
                        </div>

                        {/* Row 6: clientWebsite + taxAssessor */}
                        <div className={styles.formRow}>
                            {/* clientWebsite */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('Website') : 'Client Website'}
                                </div>
                                <TextField
                                    variant="standard"
                                    fullWidth
                                    value={addClientItem.clientWebsite}
                                    onChange={(e) =>
                                        handleAddClientChange('clientWebsite', e.target.value)
                                    }
                                    onFocus={() => handleFocus('clientWebsite')}
                                    onBlur={() => handleBlur('clientWebsite')}
                                    InputProps={inputPropsStyle}
                                    inputProps={inputStyle.style}
                                    placeholder={t ? t('Website') : 'Website'}
                                />
                            </div>

                            {/* taxAssessor */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('TaxAssessor') : 'Tax Assessor'}
                                </div>

                                <Select
                                    classNamePrefix="register-select"
                                    isRtl={direction === 'rtl'}
                                    placeholder={t ? t('TaxAssessor') : 'TaxAssessor'}
                                    options={Constants.TaxAssessorOptions}
                                    isSearchable={true}
                                    onChange={(selectedOption) => {
                                        handleAddClientChange('taxAssessor', selectedOption.value);
                                    }}
                                    value={Constants.TaxAssessorOptions.find(
                                        (option) => option.value === addClientItem.taxAssessor
                                    )}
                                />
                            </div>
                        </div>

                        {/* Row 7: toggle + IncomeTaxAdvancePercentage or Amount */}
                        <div className={styles.formRow}>
                            {/* UseIncomeTaxAdvancePercentage checkbox */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t
                                        ? t('UseIncomeTaxAdvancePercentage')
                                        : 'Use Income Tax Advance Percentage'}
                                </div>
                                <FormControlLabel
                                    className={styles.checkbox}
                                    control={
                                        <Checkbox
                                            checked={useIncomeTaxAdvancePercentage}
                                            onChange={() =>
                                                setUseIncomeTaxAdvancePercentage(
                                                    !useIncomeTaxAdvancePercentage
                                                )
                                            }
                                            color="primary"
                                        />
                                    }
                                    label=""
                                />
                            </div>

                            {/* If true => show IncomeTaxAdvancePercentage, else IncomeTaxAdvanceAmount */}
                            {useIncomeTaxAdvancePercentage ? (
                                <div className={styles.field}>
                                    <div className={styles.title}>
                                        {t
                                            ? t('IncomeTaxAdvancePercentage')
                                            : 'Income Tax Advance %'}
                                    </div>
                                    <TextField
                                        variant="standard"
                                        fullWidth
                                        type="number"
                                        value={addClientItem.incomeTaxAdvancePercentage}
                                        onChange={(e) =>
                                            handleAddClientChange(
                                                'incomeTaxAdvancePercentage',
                                                e.target.value
                                            )
                                        }
                                        onFocus={() => handleFocus('incomeTaxAdvancePercentage')}
                                        onBlur={() => handleBlur('incomeTaxAdvancePercentage')}
                                        InputProps={inputPropsStyle}
                                        inputProps={{ ...inputStyle.style, inputMode: 'numeric' }}
                                        placeholder={
                                            t
                                                ? t('IncomeTaxAdvancePercentage')
                                                : 'IncomeTaxAdvancePercentage'
                                        }
                                    />
                                </div>
                            ) : (
                                <div className={styles.field}>
                                    <div className={styles.title}>
                                        {t
                                            ? t('IncomeTaxAdvanceAmount')
                                            : 'Income Tax Advance Amount'}
                                    </div>
                                    <TextField
                                        variant="standard"
                                        fullWidth
                                        type="number"
                                        value={addClientItem.incomeTaxAdvanceAmount}
                                        onChange={(e) =>
                                            handleAddClientChange(
                                                'incomeTaxAdvanceAmount',
                                                e.target.value
                                            )
                                        }
                                        onFocus={() => handleFocus('incomeTaxAdvanceAmount')}
                                        onBlur={() => handleBlur('incomeTaxAdvanceAmount')}
                                        InputProps={inputPropsStyle}
                                        inputProps={{ ...inputStyle.style, inputMode: 'numeric' }}
                                        placeholder={
                                            t
                                                ? t('IncomeTaxAdvanceAmount')
                                                : 'IncomeTaxAdvanceAmount'
                                        }
                                    />
                                </div>
                            )}
                        </div>

                        {/* Row 8: reportingType, advancedReportingType */}
                        <div className={styles.formRow}>
                            {/* reportingType - replaced with react-select */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t
                                        ? t('SelectClientReportType')
                                        : 'Client Reporting Type'} *
                                </div>
                                <Select
                                    classNamePrefix="register-select"
                                    isRtl={direction === 'rtl'}
                                    placeholder={
                                        t
                                            ? t('SelectClientReportType')
                                            : 'Select a report type...'
                                    }
                                    options={reportingTypeOptions}
                                    value={reportingTypeOptions.find(
                                        (o) => o.value === addClientItem.reportingType
                                    )}
                                    onChange={(selectedOption) =>
                                        handleAddClientChange('reportingType', selectedOption.value)
                                    }
                                    onFocus={() => setIsReportFocus(true)}
                                    onBlur={() => setIsReportFocus(false)}
                                />
                            </div>

                            {/* advancedReportingType - replaced with react-select */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t
                                        ? t('AdvanceIncomeTaxReportType')
                                        : 'Advance Income Tax Report Type'} *
                                </div>
                                <Select
                                    classNamePrefix="register-select"
                                    isRtl={direction === 'rtl'}
                                    placeholder={t ? t('AdvanceIncomeTaxReportType') : 'Choose...'}
                                    options={reportingTypeOptions}
                                    value={reportingTypeOptions.find(
                                        (o) => o.value === addClientItem.advancedReportingType
                                    )}
                                    onChange={(selectedOption) =>
                                        handleAddClientChange(
                                            'advancedReportingType',
                                            selectedOption.value
                                        )
                                    }
                                    onFocus={() => setIsAdvancedReportFocus(true)}
                                    onBlur={() => setIsAdvancedReportFocus(false)}
                                />
                            </div>
                        </div>

                        {/* Row 9: accountManagement, caseType */}
                        <div className={styles.formRow}>
                            {/* accountManagement - replaced with react-select */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('AccountManagement') : 'Account Management'} *
                                </div>
                                <Select
                                    classNamePrefix="register-select"
                                    isRtl={direction === 'rtl'}
                                    placeholder={
                                        t ? t('SelectAccountManagement') : 'Choose manager...'
                                    }
                                    options={accountManagementOptions}
                                    value={accountManagementOptions.find(
                                        (o) => o.value === addClientItem.accountManagement
                                    )}
                                    onChange={(selectedOption) =>
                                        handleAddClientChange(
                                            'accountManagement',
                                            selectedOption.value
                                        )
                                    }
                                    onFocus={() => setIsAccountManagementFocus(true)}
                                    onBlur={() => setIsAccountManagementFocus(false)}
                                />
                            </div>

                            {/* caseType - replaced with react-select */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('CaseType') : 'Case Type'} *
                                </div>
                                <Select
                                    classNamePrefix="register-select"
                                    isRtl={direction === 'rtl'}
                                    placeholder={t ? t('CaseType') : 'Choose a case type...'}
                                    options={caseTypeOptions}
                                    value={caseTypeOptions.find(
                                        (o) => o.value === addClientItem.caseType
                                    )}
                                    onChange={(selectedOption) =>
                                        handleAddClientChange('caseType', selectedOption.value)
                                    }
                                    onFocus={() => setIsCaseTypeFocus(true)}
                                    onBlur={() => setIsCaseTypeFocus(false)}
                                />
                            </div>
                        </div>

                        {/* Row 10: package */}
                        <div className={styles.formRow}>
                            {/* package - replaced with react-select */}
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('Package') : 'Package'} *
                                </div>
                                <Select
                                    classNamePrefix="register-select"
                                    isRtl={direction === 'rtl'}
                                    placeholder={t ? t('Package') : 'Choose package...'}
                                    options={packageOptions}
                                    value={packageOptions.find(
                                        (o) => o.value === addClientItem.package
                                    )}
                                    onChange={(selectedOption) =>
                                        handleAddClientChange('package', selectedOption.value)
                                    }
                                    onFocus={() => setIsPackageFocus(true)}
                                    onBlur={() => setIsPackageFocus(false)}
                                />
                            </div>
                        </div>

                        {/* Row 11: businessField (optional) */}
                        <div className={styles.formRow}>
                            <div className={styles.field}>
                                <div className={styles.title}>
                                    {t ? t('BusinessField') : 'Business Type'} ({t ? t('Optional') : 'Optional'})
                                </div>
                                <Select
                                    classNamePrefix="register-select"
                                    isRtl={direction === 'rtl'}
                                    placeholder={t ? t('SelectBusinessField') : 'Select business type...'}
                                    options={businessFieldOptions}
                                    value={businessFieldOptions.find(
                                        (o) => o.value === addClientItem.businessField
                                    ) || null}
                                    onChange={(selectedOption) =>
                                        handleAddClientChange('businessField', selectedOption ? selectedOption.value : null)
                                    }
                                    isClearable={true}
                                    isSearchable={true}
                                />
                                <div className={styles.hint}>
                                    {t ? t('BusinessFieldOptionalDescription') : 'Optional: choose your business type only if it applies (Vehicle Dealership, Garage or Lawyer). You can leave it empty.'}
                                </div>
                            </div>
                        </div>

                        {/* FOOTER ACTIONS (Cancel + Save) */}
                        {isLoading ? (
                            <CircularProgress
                                size={24}
                                className={styles.spinner}
                            />
                        ) : (
                            <div className={styles.footer}>
                                {/* Cancel */}
                                <div
                                    onClick={handleAddClientCancel}
                                    className={styles.cancel}
                                >
                                    {t ? t('Cancel') : 'Cancel'}
                                </div>

                                {/* Save */}
                                <div
                                    onClick={handleClientSave}
                                    className={`${styles.save} ${addClientEnabled ? '' : styles.saveDisabled}`}
                                >
                                    {t ? t('Save') : 'Save'}
                                </div>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
}

export default ClientRegisterModal;
